import CircleCheck from '@uber/base-icons/circle-check';
import CircleExclamationPoint from '@uber/base-icons/circle-exclamation-point';
import { styled } from 'baseui';
import type { AccordionOverrides } from 'baseui/accordion';
import type { Theme } from 'baseui/theme';
import { ParagraphMedium, ParagraphSmall } from 'baseui/typography';
import { ICON_SIZE } from './constants';
export const StyledParagraphMedium = styled(ParagraphMedium, ({
  $theme
}) => ({
  margin: 0,
  color: $theme.colors.contentSecondary
}));
export const SOPSuggestionSection = styled('section', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale500,
  paddingTop: $theme.sizing.scale200,
  paddingBottom: $theme.sizing.scale200
}));
export const TrackingInconsistencySection = styled('section', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale500,
  paddingTop: $theme.sizing.scale200,
  paddingBottom: $theme.sizing.scale200
}));
export const HeadingContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale200,
  flexDirection: 'column',
  padding: `${$theme.sizing.scale300} ${$theme.sizing.scale600}`,
  marginBottom: $theme.sizing.scale500,
  position: 'sticky',
  top: 0,
  backgroundColor: $theme.colors.backgroundPrimary,
  zIndex: 2,
  boxShadow: $theme.lighting.shallowBelow
}));
export const ContentWrapper = styled<'section', {
  $height: string;
}>('section', ({
  $height
}) => ({
  display: 'flex',
  flexDirection: 'column',
  height: $height,
  overflow: 'hidden'
}));
export const StateContainer = styled('section', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: `0 ${$theme.sizing.scale400}`
}));
export const StyledCircleCheckIcon = styled(CircleCheck, ({
  $theme
}) => ({
  outline: `5px solid ${$theme.colors.positive}`,
  borderRadius: '50%',
  width: ICON_SIZE,
  height: ICON_SIZE,
  outlineOffset: '-3px'
}));
export const StyledWarningCircleExclamationPointIcon = styled(CircleExclamationPoint, ({
  $theme
}) => ({
  width: ICON_SIZE,
  height: ICON_SIZE,
  outline: `5px solid ${$theme.colors.negative}`,
  borderRadius: '50%',
  outlineOffset: '-3px'
}));
export const StyledInfoCircleExclamationPointIcon = styled(CircleExclamationPoint, ({
  $theme
}) => ({
  width: '40px',
  height: '40px',
  outline: `5px solid ${$theme.colors.blue400}`,
  borderRadius: '50%',
  outlineOffset: '-3px'
}));
export const TitleContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '2px'
});
export const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'UberMoveText',
  width: '-webkit-fill-available',
  justifyContent: 'space-between',
  height: 'auto',
  // fractal adds a container with overflowY as auto, in our case due to sticky the height of the container decreases so the containers becomes small and evaluate button is not at the bottom
  minHeight: 'calc(100vh - 100px)'
});
export const ActionSection = styled('section', ({
  $theme
}) => ({
  boxShadow: $theme.lighting.shallowAbove,
  padding: `${$theme.sizing.scale400} ${$theme.sizing.scale500}`,
  position: 'sticky',
  bottom: 0,
  backgroundColor: $theme.colors.backgroundPrimary,
  zIndex: 2,
  marginTop: $theme.sizing.scale200
}));
export const SuggestionsSection = styled('section', ({
  $theme
}) => ({
  padding: `0 ${$theme.sizing.scale600}`,
  overflow: 'auto'
}));
export const SuggestionContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale400,
  justifyContent: 'start',
  padding: $theme.sizing.scale0
}));
export const UpdateDismissContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: $theme.sizing.scale400,
  alignItems: 'center',
  width: '100%'
}));
export const PopoverContent = styled('div', ({
  $theme
}) => ({
  backgroundColor: $theme.colors.backgroundInverseSecondary,
  padding: $theme.sizing.scale500,
  borderRadius: $theme.sizing.scale200,
  width: '270px',
  color: $theme.colors.contentInversePrimary,
  display: 'flex',
  gap: $theme.sizing.scale200
}));
export const StyledCardParagraphSmall = styled(ParagraphSmall, {
  margin: 0,
  display: 'inline',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '130px'
});
export const PanelTitleContainer = styled('div', ({
  $theme
}: {
  $theme: Theme;
}) => ({
  paddingRight: $theme.sizing.scale700,
  display: 'flex',
  alignItems: 'center'
}));
export const ListItemOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      borderTop: `1px solid ${$theme.colors.borderOpaque}`
    })
  },
  Content: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 0,
      alignItems: 'start',
      gap: $theme.sizing.scale200,
      border: 'none',
      margin: 0,
      width: '100%',
      padding: 0
    })
  }
};
export const ListItemLabelOverrides = {
  LabelContent: {
    style: {
      textDecoration: 'line-through'
    }
  }
};
export const SuggestionLabelOverrides = {
  LabelContent: {
    style: ({
      $theme
    }) => ({
      backgroundColor: $theme.colors.backgroundPositiveLight,
      paddingTop: $theme.sizing.scale100,
      paddingBottom: $theme.sizing.scale100,
      paddingLeft: $theme.sizing.scale300,
      paddingRight: $theme.sizing.scale300,
      color: $theme.colors.contentPositive,
      border: '1px',
      borderRadius: $theme.sizing.scale100
    })
  }
};
export const accordionOverrides: AccordionOverrides = {
  Root: {
    style: {
      border: 'none'
    },
    props: {
      'data-testid': 'accordion-root'
    }
  }
};
export const getPanelOverrides = (isStaleSuggestion: boolean) => ({
  PanelContainer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      border: `${$theme.sizing.scale0} solid ${$theme.colors.borderOpaque}`,
      borderRadius: $theme.borders.radius300,
      overflow: 'hidden',
      marginBottom: $theme.sizing.scale600,
      '&:last-child': {
        marginBottom: 0
      }
    })
  },
  Header: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      padding: $theme.sizing.scale200
    })
  },
  Content: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      padding: `0 ${$theme.sizing.scale600} ${$theme.sizing.scale600}`
    })
  },
  ToggleIcon: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      backgroundColor: $theme.colors.backgroundSecondary,
      padding: $theme.sizing.scale200,
      borderRadius: '50%',
      color: isStaleSuggestion ? $theme.colors.contentStateDisabled : $theme.colors.contentSecondary
    })
  }
});
export const correctionCountOverrides = {
  BaseButton: {
    style: ({
      $theme
    }) => ({
      backgroundColor: $theme.colors.backgroundNegative,
      pointerEvents: 'none',
      cursor: 'default',
      padding: `${$theme.sizing.scale100} ${$theme.sizing.scale200}`
    })
  }
};
export const notificationOverrides = {
  Body: {
    style: {
      marginLeft: 0,
      marginRight: 0,
      width: 'auto'
    }
  },
  InnerContainer: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: $theme.sizing.scale700
    })
  }
};